import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import {
	autocompletion,
	completionKeymap,
	closeBrackets,
	closeBracketsKeymap,
} from '@codemirror/autocomplete';
import {
	defaultKeymap,
	indentWithTab as indentWithTabCommand,
	historyKeymap,
	history,
} from '@codemirror/commands';
import { css as cssExtension } from '@codemirror/lang-css';
import { html as htmlExtension } from '@codemirror/lang-html';
import {
	indentOnInput,
	defaultHighlightStyle,
	bracketMatching,
	syntaxHighlighting,
} from '@codemirror/language';
import { Compartment, EditorState, StateEffect } from '@codemirror/state';
import { oneDarkHighlightStyle } from '@codemirror/theme-one-dark';
import {
	drawSelection,
	EditorView,
	highlightActiveLine,
	highlightSpecialChars,
	keymap,
	highlightActiveLineGutter,
	lineNumbers,
} from '@codemirror/view';
import { colors } from '@atlaskit/theme';
import { useThemeObserver, token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages';

const getModeExtensions = (mode: 'css' | 'html' | 'plain' | string) => {
	switch (mode) {
		case 'css':
			return [cssExtension()];
		case 'html':
			return [htmlExtension()];
		default:
			return [];
	}
};

const getTheme = (isDarkMode: boolean, editable: boolean) =>
	EditorView.theme(
		{
			'&': {
				height: '300px',
				backgroundColor: editable
					? token('color.background.input', colors.N0)
					: token('color.background.disabled', colors.N10),
				border: `2px solid ${token('color.border.input', colors.N30)}`,
			},
			'.cm-line': {
				opacity: editable ? '1' : '0.5',
			},
		},
		{
			dark: isDarkMode,
		},
	);

const getHighLight = (isDarkMode: boolean) =>
	syntaxHighlighting(isDarkMode ? oneDarkHighlightStyle : defaultHighlightStyle);

const getThemeExtension = (
	editable: boolean,
	editorHighlight: Compartment,
	editorTheme: Compartment,
	isDarkMode: boolean,
) => [editorHighlight.of(getHighLight(isDarkMode)), editorTheme.of(getTheme(isDarkMode, editable))];

// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
const useCodeMirror = (props) => {
	const { value, onChange, mode, editable, editorRef } = props;
	const { formatMessage } = useIntl();
	const viewRef = useRef<undefined | EditorView>();
	const editorTheme = useMemo(() => new Compartment(), []);
	const editorHighlight = useMemo(() => new Compartment(), []);
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	useEffect(() => {
		viewRef.current?.dispatch({
			effects: [
				editorTheme.reconfigure(getTheme(isDarkMode, editable)),
				editorHighlight.reconfigure(getHighLight(isDarkMode)),
			],
		});
	}, [editorTheme, editable, editorHighlight, isDarkMode]);

	const updateListener = useMemo(
		() =>
			EditorView.updateListener.of((vu) => {
				if (vu.docChanged && typeof onChange === 'function') {
					const { doc } = vu.state;
					onChange(doc.toString());
				}
			}),
		[onChange],
	);
	const extensionsArray = useMemo(
		() => [
			lineNumbers(),
			highlightActiveLineGutter(),
			highlightSpecialChars(),
			drawSelection(),
			indentOnInput(),
			bracketMatching(),
			closeBrackets(),
			autocompletion(),
			highlightActiveLine(),
			history(),
			keymap.of([
				...closeBracketsKeymap,
				...defaultKeymap,
				...completionKeymap,
				...historyKeymap,
				indentWithTabCommand,
			]),
			EditorView.editable.of(editable),
			EditorView.contentAttributes.of({
				'aria-label': formatMessage(messages.codeContent),
				tabIndex: '0',
			}),
			updateListener,
			...getModeExtensions(mode),
			...getThemeExtension(editable, editorHighlight, editorTheme, isDarkMode),
		],
		[editable, updateListener, mode, editorHighlight, editorTheme, isDarkMode, formatMessage],
	);

	useEffect(() => {
		if (editorRef.current && !viewRef.current) {
			viewRef.current = new EditorView({
				state: EditorState.create({
					doc: value,
					extensions: extensionsArray,
				}),
				parent: editorRef.current,
			});
		}
		return () => {
			viewRef.current?.destroy();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const currentValue = viewRef.current?.state.doc.toString() ?? '';
		if (viewRef.current && value !== currentValue) {
			viewRef.current.dispatch({
				changes: { from: 0, to: currentValue.length, insert: value || '' },
			});
		}
	}, [value]);

	useEffect(() => {
		if (viewRef.current) {
			viewRef.current?.dispatch({ effects: StateEffect.reconfigure.of(extensionsArray) });
		}
	}, [extensionsArray]);

	return { viewRef };
};

export type ReactCodeMirrorProps = {
	isReadOnly: boolean;
	value: string;
	mode: 'css' | 'html' | 'plain';
	onChange: (arg1: string) => void;
};

export type ReactCodeMirrorElement = {
	replaceSelection: (arg1: string) => void;
	focus: () => void;
};

export const ReactCodeMirror = forwardRef<ReactCodeMirrorElement, ReactCodeMirrorProps>(
	(props, ref) => {
		const { value, mode, onChange, isReadOnly } = props;
		const editorRef = useRef<HTMLDivElement>(null);
		const { viewRef } = useCodeMirror({
			editorRef,
			value,
			onChange,
			mode,
			editable: !isReadOnly,
		});
		useImperativeHandle(
			ref,
			() => ({
				replaceSelection: (newText) => {
					if (!isReadOnly && viewRef.current !== undefined) {
						const replaceTransaction = viewRef.current.state.replaceSelection(newText);
						replaceTransaction.scrollIntoView = true;
						viewRef.current?.dispatch(replaceTransaction);
					}
				},
				focus: () => {
					if (viewRef.current !== undefined) {
						viewRef.current.focus();
					}
				},
			}),
			[viewRef, isReadOnly],
		);

		return (
			<CodeMirrorWrapper
				data-testId={`servicedesk-customer-notification-settings-templates.ui.code-mirror-wrapper-${mode}`}
			>
				<div ref={editorRef} />
			</CodeMirrorWrapper>
		);
	},
);

ReactCodeMirror.displayName = 'ReactCodeMirror';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CodeMirrorWrapper = styled.div<{ ['data-testId']: string | undefined }>({
	width: '100%',
});
